import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedIssueReasons } from "../services/reasonService";

const useFetchPaginatedIssueReasons = ({
  setIsLoading,
  page,
  pSize,
  searchTerm,
  filterIds,
  isActive,
}: {
  setIsLoading: (loading: boolean) => void;
  page: number;
  pSize: number;
  searchTerm?: string;
  filterIds?: string;
  isActive?: boolean;
}) => {
  const [reasonsData, setReasonsData] = useState<any>();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchReasons = useCallback(
    async (searchQuery?: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        setIsLoading(true);
        const result = await getPaginatedIssueReasons(
          page,
          pSize,
          searchQuery || searchTerm,
          abortControllerRef.current.signal,
          filterIds,
          isActive
        );
        setReasonsData(result);
      } catch (error: any) {
        console.error("Error fetching paginated issue reasons:", error);
        // Don't update state if request was aborted
        if (error.name !== "AbortError") {
          setReasonsData(null);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, page, pSize, searchTerm, filterIds, isActive]
  );

  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [reasonsData, fetchReasons];
};

export default useFetchPaginatedIssueReasons;
